import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { 
  UserPlus, 
  Users, 
  Shield, 
  MoreVertical,
  Trash2,
  Building2,
  Calendar,
  Mail,
  Briefcase,
  TrendingUp,
  DollarSign,
  Target
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useBrandOrganization } from '@/hooks/useBrandOrganization';
import { BrandInviteModal } from './BrandInviteModal';
import { LinkToOrganizationModal } from './LinkToOrganizationModal';
import { format } from 'date-fns';

export const BrandOrganizationTab = () => {
  const { 
    organization, 
    members, 
    invites, 
    loading, 
    isAdmin,
    currentMemberPermissions,
    stats,
    removeMember 
  } = useBrandOrganization();

  if (loading) {
    return <div className="flex items-center justify-center py-8">Loading...</div>;
  }

  if (!organization) {
    return (
      <div className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle>Brand Organization</CardTitle>
            <CardDescription>
              Create or join a brand organization to collaborate with your team.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <BrandInviteModal>
                <Button className="w-full">
                  <UserPlus className="mr-2 h-4 w-4" />
                  Create Organization & Invite Team
                </Button>
              </BrandInviteModal>
              
              <LinkToOrganizationModal>
                <Button variant="outline" className="w-full">
                  <Users className="mr-2 h-4 w-4" />
                  Join Existing Organization
                </Button>
              </LinkToOrganizationModal>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="space-y-6">
      {/* Organization Overview */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <Building2 className="h-6 w-6 text-primary" />
              </div>
              <div>
                <CardTitle className="text-xl">{organization.organization_name}</CardTitle>
                <CardDescription className="flex items-center gap-2 mt-1">
                  <Calendar className="h-3.5 w-3.5" />
                  Created {format(new Date(organization.created_at), 'MMMM d, yyyy')}
                </CardDescription>
              </div>
            </div>
            {(isAdmin || currentMemberPermissions?.can_manage_members) && (
              <BrandInviteModal>
                <Button>
                  <UserPlus className="mr-2 h-4 w-4" />
                  Invite Member
                </Button>
              </BrandInviteModal>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2">
            <Badge variant={isAdmin ? 'default' : 'secondary'}>
              {isAdmin ? 'Organization Admin' : 'Team Member'}
            </Badge>
            <Badge variant="outline">
              {members.filter(m => m.status === 'active').length + 1} members
            </Badge>
          </div>
        </CardContent>
      </Card>

      {/* Organization Admin Info (for non-admins) */}
      {!isAdmin && organization.admin_profile && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Shield className="h-4 w-4" />
              Organization Admin
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center gap-4">
              <Avatar className="h-12 w-12">
                <AvatarImage src={organization.admin_profile.avatar_url} />
                <AvatarFallback>
                  {organization.admin_profile.display_name?.charAt(0) || 'A'}
                </AvatarFallback>
              </Avatar>
              <div>
                <p className="font-medium">{organization.admin_profile.display_name}</p>
                <p className="text-sm text-muted-foreground flex items-center gap-1">
                  <Mail className="h-3.5 w-3.5" />
                  {organization.admin_profile.email}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Organization Statistics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Briefcase className="h-5 w-5 text-blue-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.totalDeals}</p>
                <p className="text-xs text-muted-foreground">Total Deals</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-green-500/10 flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-green-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.activeDeals}</p>
                <p className="text-xs text-muted-foreground">Active Deals</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <DollarSign className="h-5 w-5 text-purple-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{formatCurrency(stats.totalDealValue)}</p>
                <p className="text-xs text-muted-foreground">Total Value</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-orange-500/10 flex items-center justify-center">
                <Target className="h-5 w-5 text-orange-500" />
              </div>
              <div>
                <p className="text-2xl font-bold">{stats.totalCampaigns}</p>
                <p className="text-xs text-muted-foreground">Campaigns</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Your Permissions (for non-admins) */}
      {!isAdmin && currentMemberPermissions && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Your Permissions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {Object.entries(currentMemberPermissions).map(([key, value]) => (
                value && (
                  <Badge key={key} variant="secondary" className="justify-start">
                    {key.replace('can_', '').replace(/_/g, ' ')}
                  </Badge>
                )
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Team Members */}
      <Card>
        <CardHeader>
          <CardTitle>Team Members ({members.filter(m => m.status === 'active').length + 1})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {/* Organization Admin - Always displayed first */}
            {organization.admin_profile && (
              <div className="flex items-center justify-between p-4 border rounded-lg bg-primary/5">
                <div className="flex items-center gap-4">
                  <Avatar className="h-12 w-12">
                    <AvatarImage src={organization.admin_profile.avatar_url} />
                    <AvatarFallback>
                      {organization.admin_profile.display_name?.charAt(0) || 'A'}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{organization.admin_profile.display_name}</p>
                      <Badge variant="default" className="gap-1">
                        <Shield className="h-3 w-3" />
                        Organization Admin
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {organization.admin_profile.email}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Created organization {format(new Date(organization.created_at), 'MMM d, yyyy')}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Other Team Members */}
            {members.filter(m => m.status === 'active').map((member) => (
              <div
                key={member.id}
                className="flex items-center justify-between p-4 border rounded-lg"
              >
                <div className="flex items-center gap-4">
                  <Avatar>
                    <AvatarImage src={member.profile?.avatar_url} />
                    <AvatarFallback>
                      {member.profile?.display_name?.charAt(0) || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{member.profile?.display_name}</p>
                      <Badge variant={member.role === 'admin' ? 'default' : 'secondary'}>
                        {member.role}
                      </Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {member.profile?.email}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Joined {format(new Date(member.joined_at), 'MMM d, yyyy')}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  {(isAdmin || currentMemberPermissions?.can_manage_members) && member.role !== 'admin' && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          className="text-destructive"
                          onClick={() => removeMember(member.id)}
                        >
                          <Trash2 className="mr-2 h-4 w-4" />
                          Remove Member
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>
            ))}
            {members.filter(m => m.status === 'active').length === 0 && !organization.admin_profile && (
              <p className="text-center text-muted-foreground py-4">
                No team members yet. Invite someone to get started!
              </p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Active Invites */}
      {(isAdmin || currentMemberPermissions?.can_manage_members) && invites.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Active Invites ({invites.filter(i => !i.used_at && new Date(i.expires_at) > new Date()).length})</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {invites
                .filter(i => !i.used_at && new Date(i.expires_at) > new Date())
                .map((invite) => (
                  <div
                    key={invite.id}
                    className="flex items-center justify-between p-3 border rounded-lg"
                  >
                    <div>
                      <p className="font-mono font-medium">{invite.invite_code}</p>
                      <p className="text-xs text-muted-foreground">
                        Expires {format(new Date(invite.expires_at), 'MMM d, yyyy')}
                      </p>
                    </div>
                    <Badge variant="secondary">{invite.role}</Badge>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};